import {
  ModuleRoute,
  ModulesRouter,
  ModulesRouterProps,
} from "@acin.reactjs/acin-ui/baseApp";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AppTestEnv } from "./constants/AppConstants";
import {
  ActivateProfilePageRoute,
  AppRedirectPageRoute,
  EntitySelectPageRoute,
  ForgotPasswordPageRoute,
  LoginPageRoute,
  ResetPasswordPageRoute,
} from "./constants/AppRoutes";
import useAppContext from "./hooks/useAppContext";
import { resetSessionExpired } from "./components/session/sessionConfig";
import NotificationBarFixedPublic from "./NotificationBarFixedPublic";
import NotificationBarsComponent from "./NotificationBarsComponent";

const Router = () => {
  const {
    state: {
      session: { currentCheckTime },
      language,
    },
    methods: { logout },
  } = useAppContext();

  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isPrivate = Boolean(currentCheckTime);
  const wasLoggedIn = useRef(false);
  const [redirectingToLogin, setRedirectingToLogin] = useState(false);
  const appEnv = import.meta.env.APP_ENV as string | undefined;
  const { pathname, search } = location;

  // useLayoutEffect runs before browser paint — prevents flash of 404/public page
  // when session expires and isPrivate switches to false
  useLayoutEffect(() => {
    if (isPrivate) {
      wasLoggedIn.current = true;
    } else if (wasLoggedIn.current) {
      wasLoggedIn.current = false;
      resetSessionExpired();
      setRedirectingToLogin(true);
      navigate(LoginPageRoute);
    }
  }, [isPrivate, navigate]);

  // Clear the redirecting flag once we reach the login page
  useLayoutEffect(() => {
    if (redirectingToLogin && pathname === LoginPageRoute) {
      setRedirectingToLogin(false);
    }
  }, [pathname, redirectingToLogin]);

  useEffect(() => {
    const params = new URLSearchParams(search);

    const isResetPassword =
      pathname === ResetPasswordPageRoute &&
      params.get("token") &&
      params.get("email");

    if (isResetPassword) {
      logout();

      localStorage.clear();
      sessionStorage.clear();
    }
  }, [location, logout, navigate, pathname, search]);

  const modules = useMemo<
    ModulesRouterProps<Record<string, unknown>>["modules"]
  >(() => {
    const publicModule: ModuleRoute<Record<string, unknown>> = {
      module: "public/PublicPages",
      path: "*",
      title: t("common.mainPage"),
      bPublic: true,
    };

    const privateModule: ModuleRoute<Record<string, unknown>> = {
      module: "private/backOffice/BackOfficeSkeleton",
      bPrivate: true,
      fullScreenError: true,
      path: "*",
    };

    return [
      {
        module: "public/Authentication/AuthenticationPage",
        path: [
          LoginPageRoute,
          ForgotPasswordPageRoute,
          EntitySelectPageRoute,
          ActivateProfilePageRoute,
        ],
        title: t("private.pages.authentication.title"),
        bPublic: true,
      },
      {
        module: "public/Authentication/AuthenticationPage",
        path: ResetPasswordPageRoute,
      },
      ...[
        {
          module: "public/AppRedirectPage",
          path: AppRedirectPageRoute,
          title: t("public.appRedirect.title"),
          bPublic: true,
        },
      ],
      isPrivate ? privateModule : publicModule,
    ];
  }, [isPrivate, t]);

  return (
    <>
      {pathname !== ResetPasswordPageRoute && !isPrivate && (
        <div key={language}>
          <NotificationBarsComponent isPrivate={false} />
          {appEnv === AppTestEnv && <NotificationBarFixedPublic />}
        </div>
      )}

      {/* Skip rendering the module router while redirecting to login
          to prevent flash of 404/public page. NotificationBarsComponent
          stays mounted to avoid extra application-alerts requests. */}
      {!redirectingToLogin && <ModulesRouter modules={modules} />}
    </>
  );
};

export default Router;
